import { buildProps, definePropType } from '@ll-plus/utils'

import type { ExtractPropTypes, VNode } from 'vue'
import type Heatmap from './heatmap.vue'

import type { TooltipProps } from '../../tooltip/src/tooltip'

export interface HeatmapDataItem {
  timestamp: number
  value?: number | null
}

export type HeatmapData = HeatmapDataItem[]

export interface HeatmapTooltipSlotProps extends HeatmapDataItem {}

export interface HeatmapTooltipOptions extends Partial<TooltipProps> {
  show?: boolean
}

export interface HeatmapSlots {
  footer?: () => VNode[]
  indicator?: () => VNode[]
  'indicator-leading-text'?: () => VNode[]
  'indicator-trailing-text'?: () => VNode[]
  tooltip?: (props: HeatmapTooltipSlotProps) => VNode[]
}

export type HeatmapIndicatorVerticalPosition = 'top' | 'bottom'
export type HeatmapIndicatorHorizontalAlign = 'left' | 'center' | 'right'

export const heatmapColorThemes = {
  green: ['#9be9a8', '#40c463', '#30a14e', '#216e39'],
  blue: ['#b6e3ff', '#66b7ff', '#2f7ef7', '#1d4ed8'],
  orange: ['#fed7aa', '#fb923c', '#ea580c', '#c2410c'],
  purple: ['#e9d5ff', '#c084fc', '#9333ea', '#7c3aed'],
  red: ['#fecaca', '#f87171', '#dc2626', '#b91c1c'],
} as const

export type HeatmapColorTheme = keyof typeof heatmapColorThemes

const FIRST_DAY_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const

export const heatmapProps = buildProps({
  activeColors: {
    type: definePropType<string[]>(Array),
    default: undefined,
  },
  colorTheme: {
    type: definePropType<HeatmapColorTheme>(String),
    default: 'green',
  },
  data: {
    type: definePropType<HeatmapData>(Array),
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingData: {
    type: definePropType<HeatmapData>(Array),
    default: undefined,
  },
  minimumColor: {
    type: String,
    default: '',
  },
  fillCalendarLeading: {
    type: Boolean,
    default: false,
  },
  firstDayOfWeek: {
    type: Number,
    values: FIRST_DAY_OF_WEEK,
    default: 0,
  },
  showColorIndicator: {
    type: Boolean,
    default: true,
  },
  indicatorVerticalPosition: {
    type: definePropType<HeatmapIndicatorVerticalPosition>(String),
    default: 'bottom',
  },
  indicatorHorizontalAlign: {
    type: definePropType<HeatmapIndicatorHorizontalAlign>(String),
    default: 'right',
  },
  showMonthLabels: {
    type: Boolean,
    default: true,
  },
  showWeekLabels: {
    type: Boolean,
    default: true,
  },
  size: {
    type: definePropType<'small' | 'medium' | 'large'>(String),
    default: 'medium',
  },
  tooltip: {
    type: definePropType<HeatmapTooltipOptions>(Object),
    default: () => ({
      show: false,
      placement: 'top',
      arrow: true,
      bgColor: undefined,
      textColor: undefined,
      isWhiteBg: false,
      maxWidth: '240px',
      tipEllipsis: false,
      tipEllipsisLine: 1,
      overlayClassName: '',
      overlayStyle: {},
      ellipsis: false,
      width: '',
      containerStyle: {},
      showContainer: false,
    }),
  },
  unit: {
    type: String,
    default: '',
  },
  xGap: {
    type: definePropType<number | string>([Number, String]),
    default: undefined,
  },
  yGap: {
    type: definePropType<number | string>([Number, String]),
    default: undefined,
  },
} as const)

export type HeatmapProps = ExtractPropTypes<typeof heatmapProps>

export const heatmapMockData = (year: 'recent' | number = 'recent'): HeatmapData => {
  const result: HeatmapData = []
  const today = new Date()
  const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  const startDate =
    year === 'recent'
      ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 364)
      : new Date(year, 0, 1)

  const finalDate = year === 'recent' ? endDate : new Date(year, 11, 31)

  for (
    let current = new Date(startDate);
    current <= finalDate;
    current.setDate(current.getDate() + 1)
  ) {
    const seed = current.getDate() + current.getMonth() * 3
    const random = Math.random()
    let value = 0

    if (random > 0.65) {
      value = Math.floor(((random + seed / 31) % 1) * 12) + 1
    }

    result.push({
      timestamp: current.getTime(),
      value,
    })
  }

  return result
}

export type HeatmapInstance = InstanceType<typeof Heatmap>